import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Users, MessageCircle, BarChart3, Bell, ShoppingCart, Tag,
  Package, Star, Link2, Sparkles, EyeOff, Eye, ExternalLink,
  AlertTriangle, Info, CheckCircle2, ChevronLeft, Circle, RotateCcw,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import clsx from 'clsx';
import {
  appWidgetsApi,
  type AppWidget,
  type AppWidgetRow,
  type AppWidgetStep,
} from '../../api/endpoints/appWidgets';

/* =========================================================
   Cards installed apps contribute to the dashboard home.

   The app sends data; this file decides every pixel. That split is what keeps
   a third-party app from breaking the page — and what lets these cards move
   with the dashboard's own design instead of freezing whatever an app author
   drew.

   PLACEMENT FOLLOWS STATE, NOT CONFIGURATION. A card with something to fix or
   finish renders above the fold; once there is nothing to act on it drops back
   down among the other cards. Nobody has to choose a position, and a card
   cannot camp at the top of the page by declaring itself important.

   Nothing renders when no app has pushed anything, so a store with no apps
   sees no empty section and no placeholder.
   ========================================================= */

const ICONS: Record<string, LucideIcon> = {
  users: Users,
  message: MessageCircle,
  chart: BarChart3,
  bell: Bell,
  cart: ShoppingCart,
  tag: Tag,
  box: Package,
  star: Star,
  link: Link2,
  sparkles: Sparkles,
};

/** Snapshots older than this are labelled rather than silently trusted. */
const STALE_AFTER_MS = 6 * 60 * 60 * 1000;

/** A step still waiting on the merchant: not done by the app, not waved off. */
const isOpen = (s: AppWidgetStep) => !s.done && !s.skipped;

/**
 * Above the fold is earned by something being BROKEN or MISSING, and lost the
 * moment it is not.
 *
 * Only required steps count. An optional channel a merchant has not got round
 * to is not a reason to push the day's numbers down the page every morning —
 * that card drops into the body, checklist and all, where finishing it is still
 * one click away.
 */
const needsAttention = (w: AppWidget) =>
  Boolean(w.alert) || (w.steps || []).some((s) => isOpen(s) && s.required);

function relativeAge(iso: string, t: (k: string, d: string) => string): string | null {
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms < STALE_AFTER_MS) return null;
  const hours = Math.round(ms / 3_600_000);
  if (hours < 48) return t('appWidgets.hoursAgo', 'محدّث قبل ساعات');
  return t('appWidgets.daysAgo', 'محدّث قبل أيام');
}

function Stat({ stat }: { stat: AppWidget['stats'][number] }) {
  const value = typeof stat.value === 'number'
    // Formatted here, in the merchant's locale, rather than trusting a string
    // an app formatted in its own.
    ? stat.value.toLocaleString('en-US')
    : stat.value;

  return (
    <div className="flex flex-col gap-1 min-w-0">
      <span className="text-[11.5px] text-text-muted truncate" title={stat.hint || stat.label}>
        {stat.label}
      </span>
      <span className="flex items-baseline gap-1.5">
        <span className="text-[19px] font-bold text-text-primary num">{value}</span>
        {typeof stat.delta === 'number' && stat.delta !== 0 && (
          <span
            className={clsx(
              'text-[11.5px] font-semibold num',
              stat.delta > 0 ? 'text-emerald-600' : 'text-text-muted',
            )}
          >
            {stat.delta > 0 ? '+' : ''}{stat.delta.toLocaleString('en-US')}
          </span>
        )}
      </span>
    </div>
  );
}

const ALERT_TONES = {
  warn: { Icon: AlertTriangle, color: 'var(--color-warning)', bg: 'rgba(201,138,19,0.12)' },
  success: { Icon: CheckCircle2, color: 'var(--color-success)', bg: 'rgba(30,143,94,0.12)' },
  info: { Icon: Info, color: 'var(--color-pop-fg)', bg: 'var(--color-accent-light)' },
} as const;

function Alert({ alert }: { alert: NonNullable<AppWidget['alert']> }) {
  const tone = ALERT_TONES[alert.tone] || ALERT_TONES.info;
  const { Icon } = tone;
  return (
    <div className="flex items-start gap-2.5 rounded-xl p-3" style={{ background: tone.bg }}>
      <Icon className="w-4 h-4 shrink-0 mt-0.5" style={{ color: tone.color }} />
      <div className="flex flex-col gap-1.5 min-w-0">
        <span className="text-[12.5px] leading-relaxed text-text-secondary">{alert.text}</span>
        {alert.action && (
          <a
            href={alert.action.href}
            target={alert.action.href.startsWith('/') ? undefined : '_blank'}
            rel="noreferrer"
            className="text-[12px] font-semibold hover:underline w-fit"
            style={{ color: tone.color }}
          >
            {alert.action.label}
          </a>
        )}
      </div>
    </div>
  );
}

/** A row is a link only when the app gave one somewhere useful to go. */
function Row({ row }: { row: AppWidgetRow }) {
  const body = (
    <>
      <span className="text-[12.5px] text-text-secondary truncate" title={row.hint || row.label}>
        {row.label}
      </span>
      <span className="flex items-center gap-1 shrink-0">
        {row.value !== undefined && (
          <span className="text-[12.5px] font-semibold text-text-primary num">
            {typeof row.value === 'number' ? row.value.toLocaleString('en-US') : row.value}
          </span>
        )}
        {row.href && <ChevronLeft className="w-3.5 h-3.5 text-text-muted" />}
      </span>
    </>
  );

  const cls = 'flex items-center justify-between gap-3 py-2 border-b last:border-0';
  const style = { borderColor: 'var(--color-border-default)' };

  return row.href
    ? (
      <a
        href={row.href}
        target={row.href.startsWith('/') ? undefined : '_blank'}
        rel="noreferrer"
        className={clsx(cls, 'hover:opacity-70 transition-opacity')}
        style={style}
      >
        {body}
      </a>
    )
    : <div className={cls} style={style}>{body}</div>;
}

/* ---------------------------------------------------------
   Setup steps.

   Three states, and only one of them is the merchant's to set. `done` is the
   app's own report, recomputed on every push — a step turns green because the
   thing happened, never because a box was ticked. `skipped` is the merchant
   saying this one does not apply to their shop. What is left is the list.

   A required step has no skip. It is the app declaring it cannot work without
   it, which is a statement about the product rather than a power over the
   merchant: they can still hide the entire card.
   --------------------------------------------------------- */

function Step({ step, onSkip, onUnskip }: {
  step: AppWidgetStep;
  onSkip: () => void;
  onUnskip: () => void;
}) {
  const { t } = useTranslation();
  const resolved = step.done || step.skipped;

  return (
    <div
      className="flex items-center gap-2.5 py-2 border-b last:border-0"
      style={{ borderColor: 'var(--color-border-default)' }}
    >
      {step.done
        ? <CheckCircle2 className="w-4 h-4 shrink-0" style={{ color: 'var(--color-success)' }} />
        : <Circle className={clsx('w-4 h-4 shrink-0', step.skipped ? 'text-text-disabled' : 'text-text-muted')} />}

      <div className="flex flex-col min-w-0 flex-1">
        <span
          className={clsx(
            'text-[12.5px] truncate',
            resolved ? 'text-text-muted' : 'text-text-primary font-medium',
          )}
          title={step.hint || step.label}
        >
          {step.label}
        </span>
        {/* The hint earns its space only while the step is still open. */}
        {step.hint && !resolved && (
          <span className="text-[11.5px] text-text-muted leading-relaxed">{step.hint}</span>
        )}
      </div>

      {step.skipped ? (
        <button
          type="button"
          onClick={onUnskip}
          className="inline-flex items-center gap-1 text-[11.5px] text-text-muted hover:text-text-primary transition-colors shrink-0"
        >
          <RotateCcw className="w-3 h-3" />
          {t('appWidgets.undoSkip', 'تراجع')}
        </button>
      ) : !step.done && (
        <span className="flex items-center gap-3 shrink-0">
          {step.action && (
            <a
              href={step.action.href}
              target={step.action.href.startsWith('/') ? undefined : '_blank'}
              rel="noreferrer"
              className="inline-flex items-center gap-0.5 text-[12px] font-semibold hover:underline"
              style={{ color: 'var(--color-pop-fg)' }}
            >
              {step.action.label}
              <ChevronLeft className="w-3.5 h-3.5" />
            </a>
          )}
          {!step.required && (
            <button
              type="button"
              onClick={onSkip}
              className="text-[11.5px] text-text-muted hover:text-text-primary transition-colors"
            >
              {t('appWidgets.skip', 'تخطي')}
            </button>
          )}
        </span>
      )}
    </div>
  );
}

function Steps({ widget, onToggleSkip }: {
  widget: AppWidget;
  onToggleSkip: (stepKey: string, skip: boolean) => void;
}) {
  const { t } = useTranslation();
  const steps = widget.steps || [];
  if (!steps.length) return null;

  // Nothing left to do means nothing left to show. A wall of green ticks is a
  // trophy cabinet, and it costs the merchant the same vertical space every
  // day as the list did when it was still useful.
  if (steps.every((s) => !isOpen(s))) return null;

  const resolved = steps.filter((s) => !isOpen(s)).length;
  const pct = Math.round((resolved / steps.length) * 100);
  const complete = resolved === steps.length;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-3">
        <span className="text-[12px] font-bold text-text-primary">
          {widget.stepsTitle || t('appWidgets.setup', 'إعداد التطبيق')}
        </span>
        <span className="text-[11.5px] text-text-muted num shrink-0">
          {resolved} / {steps.length}
        </span>
      </div>

      <div
        className="h-1 rounded-full overflow-hidden"
        style={{ background: 'var(--color-bg-subtle)' }}
        role="progressbar"
        aria-valuenow={pct}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div
          className="h-full rounded-full transition-[width] duration-500"
          style={{
            width: `${pct}%`,
            background: complete ? 'var(--color-success)' : 'var(--color-accent-action)',
          }}
        />
      </div>

      <div className="flex flex-col">
        {steps.map((s) => (
          <Step
            key={s.key}
            step={s}
            onSkip={() => onToggleSkip(s.key, true)}
            onUnskip={() => onToggleSkip(s.key, false)}
          />
        ))}
      </div>
    </div>
  );
}

function WidgetCard({ widget, featured, onHide, onToggleSkip }: {
  widget: AppWidget;
  /** Above the fold, where setup leads and the numbers follow. */
  featured?: boolean;
  onHide: () => void;
  onToggleSkip: (stepKey: string, skip: boolean) => void;
}) {
  const { t } = useTranslation();
  const Icon = ICONS[widget.icon] || Sparkles;
  const age = relativeAge(widget.updatedAt, t as never);

  const stats = widget.stats.length > 0 && (
    <div
      className="grid gap-4"
      style={{ gridTemplateColumns: `repeat(${Math.min(widget.stats.length, 4)}, minmax(0, 1fr))` }}
    >
      {widget.stats.map((s) => <Stat key={s.label} stat={s} />)}
    </div>
  );

  const steps = <Steps widget={widget} onToggleSkip={onToggleSkip} />;

  return (
    <div
      className={clsx('ui-card p-5 flex flex-col gap-4', featured && 'border-s-2')}
      style={featured ? { borderInlineStartColor: 'var(--color-accent-action)' } : undefined}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2.5 min-w-0">
          <span
            className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
            style={{ background: 'var(--color-bg-subtle)' }}
          >
            <Icon className="w-4 h-4 text-text-secondary" />
          </span>
          <div className="min-w-0">
            <h2 className="text-[14px] font-bold text-text-primary truncate">{widget.title}</h2>
            <span className="text-[11px] text-text-muted truncate block">
              {widget.app.name}{age ? ` · ${age}` : ''}
            </span>
          </div>
        </div>

        <button
          type="button"
          onClick={onHide}
          title={t('appWidgets.hide', 'إخفاء هذه البطاقة')}
          aria-label={t('appWidgets.hide', 'إخفاء هذه البطاقة')}
          className="p-1.5 rounded-lg text-text-muted hover:text-text-primary transition-colors shrink-0"
        >
          <EyeOff className="w-4 h-4" />
        </button>
      </div>

      {/* Whatever the merchant came here to act on goes first. Once setup is
          finished the card is a report again and the numbers lead. */}
      {featured ? <>{widget.alert && <Alert alert={widget.alert} />}{steps}{stats}</>
        : <>{stats}{widget.alert && <Alert alert={widget.alert} />}{steps}</>}

      {widget.rows && widget.rows.length > 0 && (
        <div className="flex flex-col">
          {widget.rowsTitle && (
            <span className="text-[11.5px] font-semibold text-text-muted mb-1">{widget.rowsTitle}</span>
          )}
          {widget.rows.map((r) => <Row key={r.label} row={r} />)}
        </div>
      )}

      {widget.note && (
        <p
          className="text-[12.5px] leading-relaxed text-text-secondary rounded-xl p-3 m-0"
          style={{ background: 'var(--color-bg-subtle)' }}
        >
          {widget.note}
        </p>
      )}

      {widget.action && (
        <a
          href={widget.action.href}
          target={widget.action.href.startsWith('/') ? undefined : '_blank'}
          rel="noreferrer"
          className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-text-primary hover:underline mt-auto"
        >
          {widget.action.label}
          <ExternalLink className="w-3.5 h-3.5" />
        </a>
      )}
    </div>
  );
}

/**
 * @param slot  `top` renders only the cards with something outstanding, meant
 *              to sit high on the page; `body` renders everything else. Both
 *              read the same query, so mounting the pair costs one request.
 */
export function AppWidgets({ storeId, slot = 'body' }: { storeId: string; slot?: 'top' | 'body' }) {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const { data: widgets = [] } = useQuery({
    queryKey: ['app-widgets', storeId],
    queryFn: () => appWidgetsApi.list(storeId),
    enabled: !!storeId,
    // Apps push on their own schedule, so refetching aggressively buys nothing.
    staleTime: 5 * 60_000,
  });

  const setHidden = useMutation({
    mutationFn: (ids: string[]) => appWidgetsApi.setHidden(storeId, ids),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['app-widgets', storeId] }),
  });

  // Skips are written as a full list, so the current one is derived from what
  // the server already merged in rather than tracked separately.
  const skippedIds = useMemo(
    () => widgets.flatMap((w) => (w.steps || [])
      .filter((s) => s.skipped)
      .map((s) => `${w.id}:${s.key}`)),
    [widgets],
  );

  const setSkipped = useMutation({
    mutationFn: (ids: string[]) => appWidgetsApi.setSkippedSteps(storeId, ids),
    // Optimistic: skipping is a dismissal, and a checklist row that waits on a
    // round trip before greying out feels broken.
    onMutate: async (ids) => {
      await qc.cancelQueries({ queryKey: ['app-widgets', storeId] });
      const previous = qc.getQueryData<AppWidget[]>(['app-widgets', storeId]);
      const set = new Set(ids);
      qc.setQueryData<AppWidget[]>(['app-widgets', storeId], (old) => (old || []).map((w) => (
        w.steps
          ? {
            ...w,
            steps: w.steps.map((s) => (
              s.required ? s : { ...s, skipped: set.has(`${w.id}:${s.key}`) }
            )),
          }
          : w
      )));
      return { previous };
    },
    onError: (_e, _ids, ctx) => {
      if (ctx?.previous) qc.setQueryData(['app-widgets', storeId], ctx.previous);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ['app-widgets', storeId] }),
  });

  const toggleSkip = (widgetId: string, stepKey: string, skip: boolean) => {
    const id = `${widgetId}:${stepKey}`;
    setSkipped.mutate(skip ? [...new Set([...skippedIds, id])] : skippedIds.filter((x) => x !== id));
  };

  const { mine, hiddenHere } = useMemo(() => {
    const forSlot = widgets.filter((w) => (slot === 'top' ? needsAttention(w) : !needsAttention(w)));
    return { mine: forSlot, hiddenHere: forSlot.filter((w) => w.hidden).map((w) => w.id) };
  }, [widgets, slot]);

  const shown = mine.filter((w) => !w.hidden);

  // No apps, nothing outstanding for this slot: render nothing at all rather
  // than an empty shell or a heading with no cards under it.
  if (!mine.length) return null;

  const hiddenIds = widgets.filter((w) => w.hidden).map((w) => w.id);
  // Restores rather than previews. A greyed-out card the merchant then has to
  // find a second control on is two steps for one intention.
  const restoreHidden = () => setHidden.mutate(hiddenIds.filter((id) => !hiddenHere.includes(id)));

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <h2 className="text-[14px] font-bold text-text-primary">
          {slot === 'top'
            ? t('appWidgets.titleTodo', 'يحتاج إلى إكمال')
            : t('appWidgets.title', 'من تطبيقاتك')}
        </h2>
        {hiddenHere.length > 0 && (
          <button
            type="button"
            onClick={restoreHidden}
            className="inline-flex items-center gap-1.5 text-[11.5px] text-text-muted hover:text-text-primary transition-colors"
          >
            <Eye className="w-3.5 h-3.5" />
            {t('appWidgets.restoreHidden', 'إظهار المخفية')} ({hiddenHere.length})
          </button>
        )}
      </div>

      <div
        className={clsx(
          'grid gap-6 items-stretch',
          // A setup card is read top to bottom and its rows need the width; a
          // report card pairs happily with a neighbour.
          slot === 'top' ? 'grid-cols-1' : 'grid-cols-1 lg:grid-cols-2',
        )}
      >
        {shown.map((w) => (
          <WidgetCard
            key={w.id}
            widget={w}
            featured={slot === 'top'}
            onHide={() => setHidden.mutate([...hiddenIds, w.id])}
            onToggleSkip={(stepKey, skip) => toggleSkip(w.id, stepKey, skip)}
          />
        ))}
      </div>
    </div>
  );
}
